import { keepPreviousData } from '@tanstack/react-query';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  DirectoryHeader,
  Main,
  Padding,
  Stack,
  BusinessLogFilterKey,
  useBusinessLogQuery,
  useDirectory,
  TotalEntries,
  FilterTransformer,
  serializeValueDate,
  deserializeValueDate,
  ISODate,
  formatDate,
  FormattedDateTime,
  getIsoDateFromFormattedDateTime,
  EMPTY_PAGE,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './BusinessLogDirectory.translations.json';
import { Helmet } from 'react-helmet-async';
import { Flex, FormInstance } from 'antd';
import { FooterPagination } from '../classifier-mapping/ClassifierMappingDirectory.styles';
import { BusinessLogDirectoryTable } from './BusinessLogDirectoryTable';
import { BusinessLogFilterPane } from './filter/BusinessLogFilterPane';
import { mergeTraceIdFilter, splitTraceIdFilter } from './filter/utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'business-log-directory',
);
addTranslations(translations);

const FILTER_KEYS: BusinessLogFilterKey[] = [
  'action',
  'classifier',
  'createdAt',
  'createdBy',
  'description',
  'descriptionType',
  'logData',
  'logData2',
  'mapping',
  'type',
  'logDataValueDate',
  'logDataSubject',
  'traceId',
  'hasSharedTraceId',
];
const ARRAY_FIELDS: BusinessLogFilterKey[] = ['type', 'logDataValueDate'];

const dateRangeFields = ['createdAt'];

const serializeFilter: FilterTransformer<string, BusinessLogFilterKey> = (
  ...args
) => {
  if (args[0] === 'logData') {
    return splitTraceIdFilter(args[1]);
  }

  const [[id, value], ...rest] = Object.entries(
    serializeValueDate(args[0], args[1]),
  );

  return {
    [id]: dateRangeFields.includes(id)
      ? (value as FormattedDateTime[] | undefined)?.map(
          getIsoDateFromFormattedDateTime,
        )
      : value,
    ...Object.fromEntries(rest),
  };
};

const deserializeFilter: FilterTransformer<BusinessLogFilterKey> = (
  ...args
) => {
  const [filterId, , allFilters] = args;
  if (
    filterId === 'logData' ||
    filterId === 'traceId' ||
    filterId === 'hasSharedTraceId'
  ) {
    if (filterId !== 'logData' && allFilters.logData !== undefined) {
      return {};
    }

    return {
      logData: mergeTraceIdFilter(
        allFilters.logData,
        allFilters.traceId,
        allFilters.hasSharedTraceId,
      ),
    };
  }

  const [[id, value]] = Object.entries(deserializeValueDate(...args));

  return {
    [id]:
      id === 'logDataValueDate'
        ? value?.map((d: ISODate) => formatDate(d))
        : dateRangeFields.includes(id)
          ? (value?.split(',') as ISODate[] | undefined)?.map((d) =>
              formatDate(d),
            )
          : value,
  };
};

export const BusinessLogDirectory: FC = () => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);

  const {
    headerProps,
    paginationProps,
    queryParameters,
    setTotal,
    sidePanelFilterProps,
    tableProps,
    setTableFilters,
  } = useDirectory({
    arrayFields: ARRAY_FIELDS,
    deserializeFilter,
    initialSort: [{ id: 'createdAt', desc: true }],
    queryFilterKeys: FILTER_KEYS,
    serializeFilter,
    sidePanelFilterRef: formRef,
  });

  const { data: { content, totalElements } = EMPTY_PAGE, isFetching } =
    useBusinessLogQuery(queryParameters, {
      placeholderData: keepPreviousData,
    });

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <Main>
        <Main.Col>
          <Main.Header />
          <Main.Content padding={false} id="business-log-content">
            <ContentWrapper>
              <DirectoryHeader
                title={t('title')}
                infoIconUrl={InfoIconUrl.BUSINESS_LOG_DIRECTORY}
                {...headerProps}
                isFiltersOpen={isFiltersOpen}
                onSearch={undefined}
                onFilterToggle={() => setIsFiltersOpen((current) => !current)}
              />
              <Padding horizontal bottom>
                <Flex vertical gap="small">
                  <BusinessLogDirectoryTable
                    data={content || []}
                    isLoading={isFetching}
                    setFilters={setTableFilters}
                    {...tableProps}
                  />
                </Flex>
              </Padding>
            </ContentWrapper>
          </Main.Content>
          <Main.Footer>
            <TotalEntries count={totalElements} />
            <FooterPagination {...paginationProps} />
          </Main.Footer>
        </Main.Col>
        {isFiltersOpen && (
          <BusinessLogFilterPane
            filters={queryParameters}
            formRef={formRef}
            {...sidePanelFilterProps}
            onClose={() => setIsFiltersOpen(false)}
            showToggleCollapseButton={false}
          />
        )}
      </Main>
    </>
  );
};

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
